import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { getTripScoreColor } from './trip-utils';

/**
 * Props for the TripScoreBadge component.
 */
interface TripScoreBadgeProps {
	/** Normalized score value (0-100) to display. */
	score: number;
}

/**
 * Circular colored badge displaying a trip score.
 *
 * Renders a small circular badge with the score value and a background
 * color that indicates performance level (green >= 80, amber >= 60, red < 60).
 * Used in trip cards and headers to provide a quick visual indicator.
 *
 * @component
 * @example
 * ```tsx
 * <TripScoreBadge score={85} />
 * ```
 */
const TripScoreBadge: React.FC<TripScoreBadgeProps> = ({ score }) => {
	const theme = useTheme();
	const backgroundColor = getTripScoreColor(score, theme);

	return (
		<Box
			sx={{
				width: 44,
				height: 44,
				borderRadius: '50%',
				backgroundColor,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
				boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
			}}
		>
			<Typography
				sx={{
					color: '#FFFFFF',
					fontWeight: 'bold',
					fontSize: '16px',
				}}
			>
				{Math.round(score)}
			</Typography>
		</Box>
	);
};

export default TripScoreBadge;
